import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import { apiCall } from "../auth/AuthContext";
import { ArrowRight, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [state, setState] = useState({ status: "idle", message: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setState({ status: "error", message: "Missing reset token. Use the link from the email." });
      return;
    }
    if (form.password !== form.confirm) {
      setState({ status: "error", message: "Passwords do not match." });
      return;
    }
    if (form.password.length < 8) {
      setState({ status: "error", message: "Password must be at least 8 characters." });
      return;
    }
    setState({ status: "loading", message: "" });
    try {
      const res = await apiCall("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password: form.password }) });
      setState({ status: "success", message: res?.message || "Password updated. Redirecting to sign-in..." });
      setTimeout(() => nav("/dashboard", { replace: true }), 1500);
    } catch (err) {
      setState({ status: "error", message: err.message || "Reset failed." });
    }
  };

  const inputCls = "w-full bg-transparent border-b border-jc-gold/30 px-0 py-3 font-body text-base text-jc-white placeholder-jc-muted focus:border-jc-gold focus:outline-none";

  return (
    <BrandShell dashboardChrome>
      <section data-testid="reset-page" className="relative bg-jc-black text-jc-white min-h-[calc(100vh-5rem)] flex items-center overflow-hidden">
        <div className="absolute inset-0 jc-grid-bg" />
        <div className="relative z-10 mx-auto w-full max-w-md px-6 md:px-10 py-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold text-center mb-3">◆ New Password</p>
          <h1 className="font-display text-jc-white text-center" style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", lineHeight: 0.95 }}>
            SET A NEW <span className="text-jc-gold">PASSWORD.</span>
          </h1>
          <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="reset-form">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold">New Password</label>
              <input data-testid="reset-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="8+ characters" autoComplete="new-password" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold">Confirm Password</label>
              <input data-testid="reset-confirm" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} placeholder="Re-type password" autoComplete="new-password" />
            </div>
            <button type="submit" disabled={state.status === "loading"} data-testid="reset-submit" className="jc-btn jc-btn-primary w-full !py-4 disabled:opacity-60">
              {state.status === "loading" ? (<><Loader2 size={16} className="animate-spin" /> UPDATING...</>) : (<>UPDATE PASSWORD <ArrowRight size={16} strokeWidth={2.5} /></>)}
            </button>
            {state.status === "success" && <div data-testid="reset-success" className="font-mono text-xs uppercase tracking-[0.2em] text-jc-gold-light text-center">{state.message}</div>}
            {state.status === "error" && <div data-testid="reset-error" className="font-mono text-xs uppercase tracking-[0.2em] text-red-400 text-center">{state.message}</div>}
          </form>
          <p className="mt-10 text-center"><Link to="/dashboard" className="font-mono text-xs uppercase tracking-[0.25em] text-jc-muted hover:text-jc-gold">← Back to sign in</Link></p>
        </div>
      </section>
    </BrandShell>
  );
}
